import { useCallback, useEffect, useState } from "react";
import api from "api/api";
import AuthService from "lib/Auth";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";

// mui
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";

import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import Popper from "@mui/material/Popper";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import Typography from "@mui/material/Typography";

// icons
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Search from "@mui/icons-material/Search";
import IosShare from "@mui/icons-material/IosShare";

import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadSearchBarTextState, loadUlProductCheckedState, loadCompProductCheckedState, loadUnileverProductMetaDataFiltersState, loadFilteredCoreDataIdsFromSearchFiltersState } from "store/features/studyDataSlice";

const Accordion = styled((props) => (
    <MuiAccordion
        disableGutters
        elevation={0}
        square
        {...props}
    />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
        transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const SearchBar = ({ listedStudies = [], selectedRows = [], showFormulationButton = true }) => {
    const dispatch = useDispatch();
    const selectedStudies = useSelector((state) => state.studyDataStore.selectedStudies);
    const searchTerm = useSelector((state) => state.studyDataStore.searchBarText);
    const ulProductChecked = useSelector((state) => state.studyDataStore.ulProductChecked);
    const compProductChecked = useSelector((state) => state.studyDataStore.compProductChecked);
    const unileverProductMetaDataFilters = useSelector((state) => state.studyDataStore.unileverProductMetaDataFilters);

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const studyPageFilters = useSelector((state) => state.studyDataStore.studyPageFilters);

    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState(false);

    const [productFilters, setProductFilters] = useState([]);
    const [productFiltersLabelMap, setProductFiltersLabelMap] = useState([]);

    const [productCategoryLabels, setProductCategoryLabels] = useState([]);
    const [countryLabels, setCountryLabels] = useState([]);
    const [unileverBrandLabels, setUnileverBrandLabels] = useState([]);
    const [deodorantFormatLabels, setDeodorantFormatLabels] = useState([]);
    const [studyPeriodLabels, setStudyPeriodLabels] = useState([]);
    const [formulationURL, setFormulationURL] = useState("");

    const isDeodorants = process.env?.REACT_APP_DEODORANTS?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isDisabled = !selectedRows?.length || !formulationURL || !isDeodorants;

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    useEffect(() => {
        if (productCategoryData) {
            setProductCategoryLabels(productCategoryData.value);
        }
        if (Object.keys(studyPageFilters).length) {
            if (studyPageFilters?.country) {
                const labels = studyPageFilters.country
                    .filter((ele) => {
                        if (ele.checked === true) {
                            return ele;
                        }
                    })
                    .map((datum) => datum.value); // return id
                setCountryLabels(labels);
            }
            if (studyPageFilters?.unileverBrand) {
                const labels = studyPageFilters.unileverBrand
                    .filter((ele) => {
                        if (ele.checked === true) {
                            return ele;
                        }
                    })
                    .map((datum) => datum.value); // return id
                setUnileverBrandLabels(labels);
            }
            if (studyPageFilters?.deodorantFormat) {
                const labels = studyPageFilters.deodorantFormat
                    .filter((ele) => {
                        if (ele.checked === true) {
                            return ele;
                        }
                    })
                    .map((datum) => datum.label); // return labels
                setDeodorantFormatLabels(labels);
            }
            if (studyPageFilters?.studyPeriod) {
                const labels = studyPageFilters.studyPeriod
                    .filter((ele) => {
                        if (ele.checked === true) {
                            return ele;
                        }
                    })
                    .map((datum) => datum.label); // return labels
                setStudyPeriodLabels(labels);
            }
        }
    }, [studyPageFilters]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen((prev) => !prev);
    };

    const getCoreDataIds = (callFetchFilters = true) => {
        const _filter = {};
        Object.keys(unileverProductMetaDataFilters).map((ele) => {
            _filter[ele] = unileverProductMetaDataFilters[ele].join(",");
        });

        const selIds = selectedStudies.map((ele) => ele.id);
        const listedIds = listedStudies.map((ele) => ele.id);
        const payload = {
            filters: _filter,
            core_data_model_ids: selIds.length ? selIds : listedIds,
            search: {
                text: searchTerm,
                ul_check: ulProductChecked,
                comp_check: compProductChecked,
            },
            product_category_id: productCategoryId,
        };

        let url;
        if (isDeodorants) {
            url = process.env.REACT_APP_SERVER_PATH + `/products/product-metadata-filters/`;
        } else if (isOralCare) {
            url = process.env.REACT_APP_SERVER_PATH + `/oral-care-products/oral-care-product-metadata-filters/`;
        } else if (isHairCare) {
            url = process.env.REACT_APP_SERVER_PATH + `/hair-care-products/hair-care-product-metadata-filters/`;
        } else if (isSkinCare) {
            url = process.env.REACT_APP_SERVER_PATH + `/skin-care-products/skin-care-product-metadata-filters/`;
        } else if (isUnileverFragranceHouse) {
            url = process.env.REACT_APP_SERVER_PATH + `/unilever-fragrance-house-products/unilever-fragrance-house-product-metadata-filters/`; 
        } else {
            url = process.env.REACT_APP_SERVER_PATH + `/skin-cleansing-products/skin-cleansing-product-metadata-filters/`;
        }

        try {
            api.post(url, payload)
                .then(async (res) => {
                    dispatch(loadFilteredCoreDataIdsFromSearchFiltersState(res?.data?.core_data_model_ids || []));
                    if (callFetchFilters) {
                        fetchFilters(res?.data?.core_data_model_ids);
                    }
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (Object.keys(unileverProductMetaDataFilters).length) {
                getCoreDataIds(false);
            } else {
                dispatch(loadFilteredCoreDataIdsFromSearchFiltersState(null));
            }
        }, 500);

        return () => clearInterval(debounce);
    }, [unileverProductMetaDataFilters]);

    const fetchFilters = useCallback(
        (coreDataIdsArr = []) => {
            let url;
            if (isDeodorants) {
                url = process.env.REACT_APP_SERVER_PATH + `/products/ul-products-filters/?`;
            } else if (isOralCare) {
                url = process.env.REACT_APP_SERVER_PATH + `/oral-care-products/ul-products-filters/?`;
            } else if (isHairCare) {
                url = process.env.REACT_APP_SERVER_PATH + `/hair-care-products/ul-products-filters/?`;
            } else if (isSkinCare) {
                url = process.env.REACT_APP_SERVER_PATH + `/skin-care-products/ul-products-filters/?`;
            } else if (isUnileverFragranceHouse) {
                url = process.env.REACT_APP_SERVER_PATH + `/unilever-fragrance-house-products/ul-products-filters/?`;
            } else {
                url = process.env.REACT_APP_SERVER_PATH + `/skin-cleansing-products/ul-products-filters/?`;
            }
            const selIds = selectedStudies.map((ele) => ele.id);
            if (selIds.length) {
                const ids = selIds.join(",");
                url += `core_data_model_ids=${ids}`;
            } else if (coreDataIdsArr.length > 0) {
                const ids = coreDataIdsArr.join(",");
                url += `core_data_model_ids=${ids}`;
            } else {
                if (productCategoryLabels.length) {
                    const ids = productCategoryLabels.join(",");
                    url += `product_category=${ids}&`;
                }
                if (countryLabels.length) {
                    const ids = countryLabels.join(",");
                    url += `country=${ids}&`;
                }
                if (unileverBrandLabels.length) {
                    const ids = unileverBrandLabels.join(",");
                    url += `unilever_brand=${ids}&`;
                }
                if (deodorantFormatLabels.length) {
                    const values = deodorantFormatLabels.join(",");
                    url += `deodorant_format=${values}&`;
                }
                if (studyPeriodLabels.length) {
                    const values = studyPeriodLabels.join(",");
                    url += `study_period__title=${values}`;
                }
            }
            try {
                api.get(url)
                    .then(async (res) => {
                        setProductFilters(res.data.filters);
                        setProductFiltersLabelMap(res.data.mapping);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        },
        [selectedStudies, productCategoryLabels, countryLabels, unileverBrandLabels, deodorantFormatLabels, studyPeriodLabels]
    );

    useEffect(() => {
        fetchFilters();
    }, [fetchFilters]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    useEffect(() => {
        const buildFormulationURL = async () => {
            try {
                let datalabURL = process.env?.REACT_APP_FORMULATION_URL;
                const cdIDs = selectedRows.map((row) => row.id).join(",");
                if (cdIDs !== "" && datalabURL) {
                    await AuthService.refreshToken();
                    const accessToken = localStorage.getItem("access") || "";
                    const obscured = accessToken ? btoa(accessToken) : "";
                    const finalUrl = obscured
                        ? `${datalabURL}#cd_ids=${cdIDs}&access=${encodeURIComponent(obscured)}`
                        : `${datalabURL}#cd_ids=${cdIDs}`;
                    setFormulationURL(finalUrl);
                }
            } catch (error) {
                console.error("error", error);
            }
        };
        buildFormulationURL();
    }, [selectedRows]);

    const returnCheckboxes = (ele, option) => {
        return (
            <FormControlLabel
                key={`${option.toString()}-dashboard-filter-label`}
                sx={{
                    color: unileverProductMetaDataFilters?.[ele]?.includes(option) ? theme.palette.primary.main : theme.palette.text.primary,
                    background: unileverProductMetaDataFilters?.[ele]?.includes(option) ? "linear-gradient(270deg, rgba(0,103,239,0) 0%, rgba(0,94,239,0.06907777759541311) 78%)" : "none",
                    borderRadius: "5px",
                    width: "100%",
                    margin: "3px 0px",
                    padding: "2px",
                }}
                control={
                    <Checkbox
                        key={`${ele}-${option}-check`}
                        checked={unileverProductMetaDataFilters?.[ele]?.includes(option) || false}
                        size="8px"
                        name={option}
                        value={option}
                        // icon={<RadioButtonUncheckedRoundedIcon />}
                        // checkedIcon={<CheckCircleRoundedIcon />}
                        sx={{
                            padding: 0,
                        }}
                        onChange={(event) => handleFilterValueChange(event, ele)}
                    />
                }
                label={
                    <p
                        key={`${option}-label`}
                        style={{
                            fontSize: "12px",
                            marginBottom: 0,
                            marginLeft: "3px",
                        }}
                    >
                        {option}
                    </p>
                }
            />
        );
    };
    const returnSelectAllCheckboxes = (ele) => {
        return (
            <FormControlLabel
                key={`${ele.toString()}-dashboard-filter-label`}
                sx={{
                    color: unileverProductMetaDataFilters?.[ele]?.length === productFilters?.[ele]?.length ? theme.palette.primary.main : theme.palette.text.primary,
                    background: unileverProductMetaDataFilters?.[ele]?.length === productFilters?.[ele]?.length ? "linear-gradient(270deg, rgba(0,103,239,0) 0%, rgba(0,94,239,0.06907777759541311) 78%)" : "none",
                    borderRadius: "5px",
                    width: "100%",
                    margin: "3px 0px",
                    padding: "2px",
                }}
                control={
                    <Checkbox
                        key={`${ele}-${"all"}-check`}
                        checked={unileverProductMetaDataFilters?.[ele]?.length === productFilters?.[ele]?.length || false}
                        size="8px"
                        name={ele}
                        // icon={<RadioButtonUncheckedRoundedIcon />}
                        // checkedIcon={<CheckCircleRoundedIcon />}
                        sx={{
                            padding: 0,
                        }}
                        onChange={(event) => handleSelectAllFilterValue(event, ele)}
                    />
                }
                label={
                    <p
                        key={`${ele}-label`}
                        style={{
                            fontSize: "12px",
                            marginBottom: 0,
                            marginLeft: "3px",
                        }}
                    >
                        Select All
                    </p>
                }
            />
        );
    };

    const handleFilterValueChange = (event, filterKey) => {
        const col = filterKey;
        const value = event.target.value;
        const checked = event.target.checked;
        const datum = { ...unileverProductMetaDataFilters };

        let array = datum[col] || [];
        const index = array.indexOf(value);

        if (checked && index === -1) {
            array = [...array, value];
        } else {
            array = array.filter((item) => item !== value);
        }

        datum[col] = [...array];

        if (array.length === 0) {
            delete datum[col];
        }
        dispatch(loadUnileverProductMetaDataFiltersState(datum));
    };

    const handleSelectAllFilterValue = (event, filterKey) => {
        const col = filterKey;
        const checked = event.target.checked;
        const datum = { ...unileverProductMetaDataFilters };

        let array = [];
        if (checked) {
            array = productFilters[col] || [];
        } else {
            array = [];
        }

        datum[col] = [...array];

        if (array.length === 0) {
            delete datum[col];
        }

        dispatch(loadUnileverProductMetaDataFiltersState(datum));
    };
    const reset = () => {
        dispatch(loadSearchBarTextState(""));
        dispatch(loadCompProductCheckedState(false));
        dispatch(loadUlProductCheckedState(false));
        dispatch(loadUnileverProductMetaDataFiltersState({}));
        dispatch(loadFilteredCoreDataIdsFromSearchFiltersState(null));
        fetchFilters();
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <div
                style={{
                    display: "flex",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <input
                            type="text"
                            className="search-bar-input"
                            placeholder="Search for product metadata information"
                            value={searchTerm}
                            onChange={(e) => {
                                // setSearchTerm(e.target.value)
                                dispatch(loadSearchBarTextState(e.target.value));
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && searchTerm.length) {
                                    getCoreDataIds();
                                }
                            }}
                            style={{
                                minWidth: "350px",
                                fontSize: "12px",
                                padding: "5px 10px",
                            }}
                        />
                        <IconButton
                            aria-label="search"
                            color="primary"
                            // style={{
                            //     height: "29px",
                            // }}
                            sx={{
                                border: "1px solid #005eef",
                                borderLeft: "none",
                                borderRadius: 0,
                                padding: "4px 2px",
                            }}
                            onClick={() => (searchTerm.length ? getCoreDataIds() : "")}
                        >
                            <Search
                                style={{
                                    fontSize: 20,
                                }}
                            />
                        </IconButton>
                        <IconButton
                            aria-label="search"
                            color="primary"
                            // style={{
                            //     height: "29px",
                            // }}
                            sx={{
                                border: "1px solid #005eef",
                                borderLeft: "none",
                                borderRadius: 0,
                                padding: "4px 2px",
                            }}
                            onClick={reset}
                        >
                            <RestartAltIcon
                                style={{
                                    fontSize: 20,
                                }}
                            />
                        </IconButton>
                    </div>
                    <div
                        style={{
                            display: "flex",
                        }}
                    >
                        <FormGroup
                            style={{
                                marginRight: 10,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    "& .MuiTypography-root": {
                                        fontSize: 13,
                                        margin: "0px 0px",
                                        padding: "2px 0px 0px 0px",
                                        display: "block",
                                    },
                                }}
                                style={{
                                    margin: "0px 0px 0px 0px",
                                    textAlign: "left",
                                }}
                                control={
                                    <Checkbox
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 18,
                                                padding: 0,
                                            },
                                        }}
                                        style={{
                                            padding: "4px 4px 4px 0px",
                                        }}
                                        color="primary"
                                        checked={ulProductChecked}
                                        onChange={(e) => {
                                            dispatch(loadUlProductCheckedState(e.target.checked));
                                        }}
                                    />
                                }
                                label="Unilever Product"
                            />
                        </FormGroup>
                        <FormGroup
                            style={{
                                marginRight: 10,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    "& .MuiTypography-root": {
                                        fontSize: 13,
                                        margin: "0px 0px",
                                        padding: "2px 0px 0px 0px",
                                        display: "block",
                                    },
                                }}
                                style={{
                                    margin: "0px 0px 0px 0px",
                                    textAlign: "left",
                                }}
                                control={
                                    <Checkbox
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 18,
                                                padding: 0,
                                            },
                                        }}
                                        style={{
                                            padding: 4,
                                        }}
                                        color="primary"
                                        checked={compProductChecked}
                                        onChange={(e) => {
                                            dispatch(loadCompProductCheckedState(e.target.checked));
                                        }}
                                    />
                                }
                                label="Benchmark Product"
                            />
                        </FormGroup>
                    </div>
                </div>
                <div
                    style={{
                        marginLeft: 20,
                    }}
                >
                    <Button
                        className="custom-btn custom-btn-outlined color-blue"
                        size="small"
                        variant="outlined"
                        onClick={(e) => handleClick(e)}
                        style={{
                            height: "28px",
                            padding: 0,
                            fontSize: "0.8rem",
                        }}
                        endIcon={!open ? <KeyboardArrowDownIcon /> : <ExpandLessIcon />}
                    >
                        Unilever Product Metadata Filter
                    </Button>
                    <Popper
                        sx={{ zIndex: 2 }}
                        open={open}
                        anchorEl={anchorEl}
                        placement={"bottom-start"}
                        // transition
                        style={{
                            width: 330,
                            maxHeight: 580,
                            overflowY: "scroll",
                            border: "1px solid #eee",
                        }}
                    >
                        <Box sx={{ bgcolor: "background.paper" }}>
                            {productFilters &&
                                Object.keys(productFilters)?.map((ele, index) => {
                                    return (
                                        <Accordion
                                            // defaultExpanded
                                            key={`fil-2-accordion-panel-${index}-header-${index}`}
                                            sx={{
                                                background: "#dbdbdb2b",
                                                minHeight: "unset",
                                                margin: 0,
                                            }}
                                            style={{
                                                padding: 2,
                                            }}
                                        >
                                            <AccordionSummary
                                                aria-controls={`panel-${index}-content`}
                                                id={`panel-${index}-header`}
                                                style={{
                                                    minHeight: "unset",
                                                    padding: 2,
                                                    margin: 0,
                                                }}
                                                sx={{
                                                    margin: 0,
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        fontSize: "13px",
                                                        // color: Object.keys(
                                                        //     selectedFilters
                                                        // ).includes(question.label)
                                                        //     ? "#1976d2"
                                                        //     : "initial",
                                                    }}
                                                    sx={{
                                                        margin: 0,
                                                        paddingLeft: "4px",
                                                    }}
                                                >
                                                    {productFiltersLabelMap?.[ele] || ele}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    padding: "0px 0px 0px 20px ",
                                                }}
                                            >
                                                {returnSelectAllCheckboxes(ele)}

                                                {productFilters[ele].map((option) => {
                                                    return (
                                                        <div
                                                            key={option}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                padding: "0px 5px",
                                                            }}
                                                        >
                                                            {returnCheckboxes(ele, option)}
                                                        </div>
                                                    );
                                                })}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                        </Box>
                    </Popper>
                </div>
            </div>
            {showFormulationButton && (
                <div style={{ opacity: isDisabled ? 0.5 : 1 }}>
                    <Button
                        component={RouterLink}
                        className={`custom-btn custom-btn-outlined ${isDisabled ? "color-disabled" : "color-white background-color-blue"}`}
                        size="small"
                        variant="outlined"
                        sx={{
                            "& .MuiButton-startIcon": {
                                marginBottom: "5px",
                            },
                        }}
                        to={formulationURL}
                        target="_blank"
                        rel="noopener"
                        disabled={isDisabled}
                        style={{
                            height: "28px",
                            padding: 0,
                            fontSize: "0.8rem",
                        }}
                        startIcon={
                            <IosShare
                                className={`${isDisabled ? "color-disabled" : "color-white"}`}
                                style={{ fontSize: 18 }}
                            />
                        }
                    >
                        Generate Formulation
                    </Button>
                </div>
            )}
        </div>
    );
};

SearchBar.propTypes = {
    listedStudies: PropTypes.array,
    selectedRows: PropTypes.array,
    showFormulationButton: PropTypes.bool,
};

export default SearchBar;
